import { Button } from '@/components/ui/button'
import { useEditorStore } from 'CanvasCore/uiSlice'
import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import ChangeDLC from './Cpt/ChangeDLC'
import ChangeLayer from './Cpt/ChangeLayer'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ToastAction } from '@/components/ui/toast'
import { useToast } from '@/components/ui/use-toast'
import {
  ClipboardIcon,
  TrashIcon,
  BookmarkIcon,
  DotsHorizontalIcon,
  DownloadIcon,
  ImageIcon,
} from '@radix-ui/react-icons'
import { ThumbsUp } from 'lucide-react'
import { delFetcher, putFetcher } from 'api/swr'
import { optimisticRemoveComponents } from 'api'
import { cptThumb } from 'commons/cdn'
import useSWR, { mutate } from 'swr'
import useSWRMutation from 'swr/mutation'
import UpdateCoverItem from './Cpt/UpdateCover'
import CoverProgressDialog from './CoverProgressDialog'
import { useStorylineStore } from 'Editor/RightPanel/Storyline/store'

async function downloadCptAsset(assetId, filename) {
  if (!assetId) throw new Error('missing asset id')
  const url = cptThumb(assetId)
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`download failed: ${response.status}`)
  }
  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(downloadUrl)
}

const DataItemActions = ({ cpt, cpts }) => {
  const [open, setOpen] = useState(false)

  if (!cpt) return null
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
        >
          <DotsHorizontalIcon className="h-4 w-4" />
          <span className="sr-only">Actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        onInteractOutside={() => setOpen(false)} // 点击外部时关闭菜单
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            onSelect={() => navigator.clipboard.writeText(cpt.id)}
          >
            <ClipboardIcon className="mr-2 h-4 w-4" /> Copy ID
          </DropdownMenuItem>
          {/* <ChangeCover cpt={cpt} /> */}
          {/* Link style / Update Parent：暂无人使用，先隐藏 */}
          {/* <BindStyle cpt={cpt} cpts={cpts} /> */}
          <ChangeDLC cpt={cpt} />
          <ChangeLayer cpt={cpt} cpts={cpts} />
          <ToggleRecommend cpt={cpt} />
          <UpdateCoverItem cpt={cpt} />
          <SwitchAutoCover cpt={cpt} />
          <DownloadCptAssets cpt={cpt} />
          {/* <UpdateParent cpt={cpt} cpts={cpts} /> */}
          <AddToStoryline cptId={cpt.id} />
        </DropdownMenuGroup>

        <DropdownMenuLabel className="text-destructive mt-2">
          Please pay attention
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DeleteButton cpt={cpt} cpts={cpts} />
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const DeleteButton = ({ cpt, cpts }) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const childIds = (cpts ?? [])
    .filter((item) => item.pid === cpt.id)
    .map((item) => item.id)

  const { trigger: undo } = useSWRMutation(
    `/components/${cpt.id}`,
    putFetcher,
    {
      onSuccess: () => {
        mutate(['/components', { tid }])
        toast({
          title: 'Undo Successful',
          description: 'The component has been restored.',
          className: 'bg-green-500 text-white',
        })
      },
    },
  )

  const { trigger, isMutating } = useSWRMutation(`/components`, delFetcher, {
    onSuccess: () => {
      deselectCpt(tid, cpt.id)
      optimisticRemoveComponents(tid, [cpt.id, ...childIds])
      toast({
        title: 'Component Deleted',
        description: 'The component has been successfully deleted.',
        className: 'bg-green-500 text-white',
        action: (
          <ToastAction
            altText="Undo"
            onClick={() => {
              undo({ tid, key: 'state', value: 1 })
            }}
          >
            Undo
          </ToastAction>
        ),
      })
    },
  })
  const deselectCpt = useEditorStore((state) => state.deselectCpt)

  //TODO: confirm
  const handleDelete = () => {
    trigger({ id: cpt.id })
  }

  return (
    <DropdownMenuItem onClick={handleDelete} className="text-destructive">
      <TrashIcon className="mr-2" /> Delete it{isMutating && 'ing...'}
    </DropdownMenuItem>
  )
}

const isRecommended = (cpt) => Number(cpt?.recommend) === 1

const ToggleRecommend = ({ cpt }) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const recommended = isRecommended(cpt)
  const [saving, setSaving] = useState(false)

  const handleToggle = async (e) => {
    e.preventDefault()
    if (saving) return
    const next = recommended ? 0 : 1
    setSaving(true)
    try {
      await putFetcher(`/components/${cpt.id}`, {
        arg: { tid, key: 'recommend', value: next },
      })
      // 就地改字段，不整表 revalidate，避免列表顺序被重新拉乱
      await mutate(
        ['/components', { tid }],
        (current) => {
          if (!Array.isArray(current)) return current
          return current.map((item) => {
            if (item.id !== cpt.id) return item
            if (next) return { ...item, recommend: 1 }
            const nextItem = { ...item }
            delete nextItem.recommend
            return nextItem
          })
        },
        { revalidate: false },
      )
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '推荐状态更新失败',
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <DropdownMenuItem disabled={saving} onSelect={handleToggle}>
      <ThumbsUp
        className={`mr-2 h-4 w-4 ${
          recommended ? 'fill-yellow-400 text-yellow-400' : ''
        }`}
      />
      {saving ? '更新中...' : recommended ? '取消推荐' : '设为推荐'}
    </DropdownMenuItem>
  )
}

const SwitchAutoCover = ({ cpt }) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const [progress, setProgress] = useState({
    open: false,
    current: 0,
    total: 0,
    label: '',
  })

  const apply = async (value, label) => {
    if (progress.open) return
    setProgress({ open: true, current: 0, total: 1, label })
    try {
      await putFetcher(`/components/${cpt.id}`, {
        arg: { tid, key: 'autoCover', value },
      })
      setProgress({ open: true, current: 1, total: 1, label })
      await mutate(['/components', { tid }])
      toast({
        title: 'Cover 已更新',
        description: label,
        className: 'bg-green-500 text-white',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Cover 更新失败',
        description: err instanceof Error ? err.message : String(err),
      })
    } finally {
      setProgress({ open: false, current: 0, total: 0, label: '' })
    }
  }

  const hasOriginal = Boolean(cpt.original)
  const isMutating = progress.open

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <ImageIcon className="mr-2 h-4 w-4" /> 切换 Cover 类型
        </DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent>
            <DropdownMenuItem
              disabled={isMutating}
              onSelect={(e) => {
                e.preventDefault()
                apply(0, 'No HD Cover')
              }}
            >
              No HD Cover
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={isMutating || !hasOriginal}
              onSelect={(e) => {
                e.preventDefault()
                apply(1, 'HD Cover')
              }}
            >
              HD Cover{!hasOriginal ? '（无原图）' : ''}
            </DropdownMenuItem>
            <DropdownMenuItem
              disabled={isMutating || !hasOriginal}
              onSelect={(e) => {
                e.preventDefault()
                apply(2, 'HD Cover + BG')
              }}
            >
              HD Cover + BG{!hasOriginal ? '（无原图）' : ''}
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
      <CoverProgressDialog
        open={progress.open}
        current={progress.current}
        total={progress.total}
        label={progress.label}
      />
    </>
  )
}

const DownloadCptAssets = ({ cpt }) => {
  const { toast } = useToast()
  const cropW = Number(cpt.crop?.w)
  const cropH = Number(cpt.crop?.h)
  const canvasSizeLabel =
    cropW > 0 && cropH > 0 ? `${cropW}×${cropH}` : '画布'

  const runDownload = async (assetId, filename, label) => {
    if (!assetId) {
      toast({
        variant: 'destructive',
        title: '无法下载',
        description: `${label}不存在`,
      })
      return
    }
    try {
      await downloadCptAsset(assetId, filename)
      toast({
        title: '下载已开始',
        description: label,
        className: 'bg-green-500 text-white',
      })
    } catch (err) {
      toast({
        variant: 'destructive',
        title: '下载失败',
        description: err instanceof Error ? err.message : String(err),
      })
    }
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <DownloadIcon className="mr-2 h-4 w-4" /> 下载图片
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          <DropdownMenuItem
            disabled={!cpt.original}
            onSelect={(e) => {
              e.preventDefault()
              runDownload(cpt.original, `${cpt.id}-original.webp`, '原图')
            }}
          >
            下载原图
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!cpt.id}
            onSelect={(e) => {
              e.preventDefault()
              runDownload(
                cpt.id,
                `${cpt.id}-canvas.webp`,
                `组件图（编辑器 ${canvasSizeLabel}）`,
              )
            }}
          >
            下载组件图（编辑器 {canvasSizeLabel}）
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={!cpt.cover}
            onSelect={(e) => {
              e.preventDefault()
              runDownload(cpt.cover, `${cpt.id}-cover.webp`, 'Cover')
            }}
          >
            下载 Cover
          </DropdownMenuItem>
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

const AddToStoryline = ({ cptId }) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const { data: template } = useSWR([`/templates/${tid}`])
  const chapters = useStorylineStore((s) => s.chapters)
  const baseCptIds = useStorylineStore((s) => s.baseCptIds)
  const toggleBaseCpt = useStorylineStore((s) => s.toggleBaseCpt)
  const updateChapter = useStorylineStore((s) => s.updateChapter)

  if (!template?.storyline) return null

  const handleAddToBase = (e) => {
    e.preventDefault()
    toggleBaseCpt(cptId)
    const isRemoving = baseCptIds.includes(cptId)
    toast({
      title: isRemoving ? '已从基础组件移除' : '已添加到基础组件',
      className: 'bg-green-500 text-white',
    })
  }

  const handleAddToChapter = (e, index) => {
    e.preventDefault()
    const chapter = chapters[index]
    if (chapter.includes(cptId)) {
      updateChapter(index, chapter.filter((id) => id !== cptId))
      toast({
        title: `已从第 ${index + 1} 章移除`,
        className: 'bg-green-500 text-white',
      })
    } else {
      updateChapter(index, [...chapter, cptId])
      toast({
        title: `已添加到第 ${index + 1} 章`,
        className: 'bg-green-500 text-white',
      })
    }
  }

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <BookmarkIcon className="w-4 h-4 mr-2" /> 故事线
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent>
          <DropdownMenuItem onSelect={handleAddToBase}>
            {baseCptIds.includes(cptId) ? '✓ ' : ''}基础五官组件
          </DropdownMenuItem>
          {chapters.map((ch, i) => (
            <DropdownMenuItem key={i} onSelect={(e) => handleAddToChapter(e, i)}>
              {ch.includes(cptId) ? '✓ ' : ''}第 {i + 1} 章
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  )
}

export default DataItemActions
